import {toLaunchDarklyAgentSuccessGoalName} from 'common/constants'
import {createAction, handleActions} from 'redux-actions'
import client from 'common/util/http'
import {loadError, loadFinish, loadStart} from 'common/redux/root'
import {replace} from 'react-router-redux'
import toastr from 'toastr'

const RESET = 'referral/RESET'
const LOAD = 'referral/LOAD'
const INVITE_PROVIDER_ERROR = 'referral/INVITE_PROVIDER_ERROR'
const PLACE_QUALIA_ORDER_ERROR = 'referral/PLACE_QUALIA_ORDER_ERROR'
const UPDATE_TITLE_QUESTIONS = 'referral_status/UPDATE_TITLE_QUESTIONS'

export const load = createAction(LOAD)
const reset = createAction(RESET)
const setInviteProviderError = createAction(INVITE_PROVIDER_ERROR)
const setPlaceQualiaOrderError = createAction(PLACE_QUALIA_ORDER_ERROR)
const updateTitleQuestions = createAction(UPDATE_TITLE_QUESTIONS)


const defaultErrorHandler = dispatch => error => {
  dispatch(loadError(error))
  return Promise.reject(error)
}

const defaultLoadHandler = dispatch => response => {
    dispatch(load(response))
    dispatch(loadFinish())
    return Promise.resolve(response)
}

export const fetchReferral = (hash, reset_=false) => async dispatch => {
    if (reset_) {
      dispatch(reset())
    }

    dispatch(loadStart())
    try {
      const response = await client.post('referral', {
        data: {
          hash
        }
      })
      dispatch(load(response))
      dispatch(loadFinish())
      return response
    } catch (err) {
      dispatch(loadError(err))
    }
}

export function trackActivity(hash, type) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('track_referral_activity', {
      data: {
        hash,
        type
      }
    })
    .then(dispatch(defaultLoadHandler))
    .catch(dispatch(defaultErrorHandler))
  }
}

/**
 * LaunchDarkly does not provide a way to wait for the ldClient call to finish since `ldClient.track` return void. We call the
 * server to track this goal so we can wait for the call to return.
 */
export function trackAgentSuccessClick({ stage, phoneNumber, url, videoUrl  }) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('track_goal', {
      data: {
        goal_key: toLaunchDarklyAgentSuccessGoalName({ phoneNumber, stage, url, videoUrl }),
        goal_data: {
          stage,
          phoneNumber,
          url,
          videoUrl,
        },
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function createSettlementProviderSelection(
  assignment_transaction_hash,
  settlement_provider_name,
  searchConfig,
  settlement_provider_address,
  qualia_settlement_agency_external_id,
  distance_from_search_origin,
) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('save_settlement_provider_selection', {
      data: {
        assignment_transaction_hash,
        settlement_provider_name,
        settlement_provider_address,
        qualia_agency_id: qualia_settlement_agency_external_id,
        provider_distance_from_origin: distance_from_search_origin,
        search_origin_address: searchConfig.origin_address,
        search_origin_city: searchConfig.origin_city,
        search_origin_state: searchConfig.origin_state,
        search_origin_zip: searchConfig.origin_zipcode,
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function updateSettlementProviderSelectionInfo(assignment_transaction_hash, settlement_provider_name) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('qualia/update_settlement_provider_selection', {
      data: {
        assignment_transaction_hash,
        settlement_provider_name,
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function removeSettlementProviderSelection(assignment_transaction_hash) {
  return dispatch => {
    dispatch(loadStart())
    return client.post('remove_settlement_provider_selection', {
      data: {
        assignment_transaction_hash,
      }
    })
      .then(dispatch(defaultLoadHandler))
      .catch(dispatch(defaultErrorHandler))
  }
}

export function sharePaymentInformation(email, referral_name) {
  return dispatch => {
    return client.post('share_payment_info', {
      data: {
        email,
        referral_name
      }
    })
    .catch(dispatch(defaultErrorHandler))
  }
}

export const updateTransaction = (hash, payload) => async dispatch => {
    dispatch(loadStart())
    try {
      const response = await client.post('update_transaction', {
        data: {
          hash,
          ...payload
        }
      })
      dispatch(load(response))
      dispatch(loadFinish())

      if (payload.type === 'add') {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(`transaction-added:${response.edited_transaction_hash}`)
        }
        dispatch(replace({
          pathname: '/'
        }))
      } else {
        dispatch(closeEditDrawer())
      }
      return response
    } catch (err) {
      if (!isEmbedded) {
        const msg = err.error.msg || "An unknown error has occurred"
        toastr.error(msg)
      }
      dispatch(loadError(err))
    }
}

export const updateContact = (hash, payload) => async dispatch => {
  dispatch(loadStart())
  try {
    const response = await client.post('update_contact', {
      data: {
        hash,
        ...payload
      }
    })
    dispatch(load(response))
    dispatch(loadFinish())
    dispatch(closeEditDrawer())
    return response
  } catch (err) {
    dispatch(loadError(err))
  }
}

export function sendProviderInvite(hash, provider_info) {
  return async (dispatch) => {
    dispatch(loadStart())
    resetInviteProviderError()
    try {
      const response = await client.post('qualia/invite_settlement_agency', {
        data: {
          hash,
          ...provider_info,
        }
      })
      dispatch(load(response))
      return true
    } catch (error) {
        dispatch(setInviteProviderError(error))
      return false
    } finally {
      dispatch(loadFinish())
    }
  }
}

export const resetInviteProviderError = () => dispatch => {
  dispatch(setInviteProviderError(null))
}

export function placeQualiaOrder(hash, place_order_request) {
  return async (dispatch) => {
    dispatch(loadStart())
    resetPlaceQualiaOrderError()
    try {
      const response = await client.post('qualia/place_open_order', {
        data: {
          hash,
          ...place_order_request,
        }
      })
      dispatch(load(response))
      return true
    } catch (error) {
        dispatch(setPlaceQualiaOrderError(error))
      return false
    } finally {
      dispatch(loadFinish())
    }
  }
}

export const resetPlaceQualiaOrderError = () => dispatch => {
  dispatch(setPlaceQualiaOrderError(null))
}

export const closeEditDrawer = () => dispatch => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage('dismiss-modal')
  }

  dispatch(replace({
    hash: window.location.hash,
    pathname: window.location.pathname,
    query: _.omit(window.location.query, 'edit', 'contact', 'add')
  }))
}

export function updateAgentTitleQuestions(question_answer_dict) {
  return dispatch => {
    dispatch(updateTitleQuestions(question_answer_dict))
  }
}

const initialState = {}

export default handleActions({
    [LOAD]: (state, { payload }) => ({ ...state, ...payload }),
    [RESET]: (state, { payload }) => ({ ...initialState }),
    [INVITE_PROVIDER_ERROR]: (state, { payload }) =>({ ...state, inviteProviderError: payload }),
    [PLACE_QUALIA_ORDER_ERROR]: (state, { payload }) =>({ ...state, placeQualiaOrderError: payload }),
    [UPDATE_TITLE_QUESTIONS]: (state, {payload}) => ({
        ...state,
        referral: {
          ...state.referral,
          title_questions: {...state.referral.title_questions, ...payload}
        }
    }),
}, initialState)
